"use client";

import { useSyncExternalStore, type CSSProperties } from "react";

/**
 * Flat 2D playing-card spade (unicode suit) — always “faces” the camera,
 * no faux-3D taper like some SVG paths.
 */
function SpadeGlyph({
  className,
  fontPx,
}: {
  className?: string;
  fontPx: number;
}) {
  return (
    <span
      className={`flex h-full w-full select-none items-center justify-center font-serif leading-none ${className ?? ""}`}
      style={{ fontSize: `${fontPx}px`, lineHeight: 1 }}
      aria-hidden
    >
      ♠
    </span>
  );
}

const SPADE_COUNT = 18;

function subscribeMotion(cb: () => void): () => void {
  const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
  mq.addEventListener("change", cb);
  return () => mq.removeEventListener("change", cb);
}

function getReducedSnapshot(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function getReducedServer(): boolean {
  return false;
}

/**
 * Whimsical spade suits drifting upward — billboard-style (no rotation).
 */
export function FloatingSpadesLayer({ duel }: { duel: boolean }) {
  const reduced = useSyncExternalStore(
    subscribeMotion,
    getReducedSnapshot,
    getReducedServer,
  );

  if (reduced) return null;

  return (
    <div
      className="pointer-events-none fixed inset-0 -z-[15] overflow-hidden"
      aria-hidden
    >
      {Array.from({ length: SPADE_COUNT }, (_, i) => {
        const w = 12 + (i % 7) * 3;
        const h = 14 + (i % 7) * 3;
        const fontPx = Math.round(Math.min(w, h) * 0.92);
        return (
          <span
            key={`spade-${i}`}
            className={`cwf-floating-spade absolute block ${duel ? "text-sky-700/45" : "text-sky-600/40"}`}
            style={
              {
                left: `${((i * 47 + 11) % 86) + 7}%`,
                bottom: "-6%",
                animationDelay: `${(i * 0.37) % 7.5}s`,
                animationDuration: `${13 + (i % 11) * 1.25}s`,
                width: `${w}px`,
                height: `${h}px`,
                "--cwf-spade-opacity": duel
                  ? 0.12 + (i % 4) * 0.028
                  : 0.14 + (i % 5) * 0.022,
              } as CSSProperties
            }
          >
            <SpadeGlyph fontPx={fontPx} />
          </span>
        );
      })}
    </div>
  );
}

const CLIP_SPADE_COUNT = 14;

/** Spades clipped to a relative parent (e.g. trivia answer panel) — matches shell styling. */
export function SpadesBackdropClip({ className }: { className?: string }) {
  const reduced = useSyncExternalStore(
    subscribeMotion,
    getReducedSnapshot,
    getReducedServer,
  );

  if (reduced) return null;

  return (
    <div
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className ?? ""}`}
      aria-hidden
    >
      {Array.from({ length: CLIP_SPADE_COUNT }, (_, i) => {
        const w = 10 + (i % 6) * 2;
        const h = 12 + (i % 6) * 2;
        const fontPx = Math.round(Math.min(w, h) * 0.92);
        return (
          <span
            key={`clip-spade-${i}`}
            className="cwf-floating-spade absolute block text-sky-400/38"
            style={
              {
                left: `${((i * 53 + 19) % 88) + 6}%`,
                bottom: "-8%",
                animationDelay: `${(i * 0.41) % 6.5}s`,
                animationDuration: `${11 + (i % 9) * 1.1}s`,
                width: `${w}px`,
                height: `${h}px`,
                "--cwf-spade-opacity": 0.1 + (i % 5) * 0.026,
              } as CSSProperties
            }
          >
            <SpadeGlyph fontPx={fontPx} />
          </span>
        );
      })}
    </div>
  );
}
